import { useEffect, useRef, useState } from 'react'
import type { SelectedNbfFilters } from '@which/shared'

import { getMappedFilterQueryParams } from '../../../../pages/reviews/product-listing/utils/get-mapped-filter-query-params'
import { useRegex } from '../../../../shared/hooks/useRegex'
import type { FilterDataMap, FiltersSnapshotMap, ShowMoreFilters } from '../../../../shared/types'
import { merge } from '../../../../shared/utils/merge'
import { getFilterAttributeValue } from '../components/Filter/utils'
import { checkToFromValues } from '../utils/check-to-from-values'
import { updateNestedValue } from '../utils/update-nested-value'
import { useFilterQueryParams } from './useFilterQueryParams'
import { useNeedsBasedFilters } from './useNeedsBasedFilters'
import { useProductListingPageOverlayFilters } from './useProductListingPageOverlayFilters'

export const useFiltersStates = () => {
  const { getQueryParamValue } = useRegex()
  const { getFilterQueryParams } = useFilterQueryParams()
  const [
    {
      basicFilterDataFromQueryParams,
      needsBasedQueryValuesFromQueryParams,
      needsBasedFilterDataFromQueryParams,
      needsBasedOptionsFromQueryParams,
      needsBasedLabelsFromQueryParams,
      needsBasedSelectedMapFromQueryParams,
    },
  ] = useState<ReturnType<typeof getMappedFilterQueryParams>>(() =>
    getMappedFilterQueryParams(getFilterQueryParams, getQueryParamValue)
  )

  const [filterDataMap, setFilterDataMap] = useState<FilterDataMap>({
    basic: basicFilterDataFromQueryParams,
    needsBased: needsBasedFilterDataFromQueryParams,
    needsBasedQueryValues: needsBasedQueryValuesFromQueryParams,
  })

  const [filtersToRemoveMap, setFiltersToRemoveMap] = useState<FilterDataMap>({
    basic: {},
    needsBased: {},
    needsBasedQueryValues: {},
  })

  const [filtersSnapshotMap, setFiltersSnapshotMap] = useState<FiltersSnapshotMap>({
    basic: {},
    needsBased: {},
    needsBasedQueryValues: {},
    currentNeedsBasedOptions: {},
  })

  const [needsBasedSelectedMap, setNeedsBasedSelectedMap] = useState<SelectedNbfFilters>(
    needsBasedSelectedMapFromQueryParams
  )

  const [showMoreFilters, setShowMoreFilters] = useState<ShowMoreFilters>({})

  const [pageNumberOnFilterInteraction, setPageNumberOnFilterInteraction] = useState<number | null>(
    null
  )

  const { isOverlayFiltersDisplayed, setIsOverlayFiltersDisplayed } =
    useProductListingPageOverlayFilters()

  const {
    handleNeedsBasedFilters,
    setPreviousNeedsBasedFilterLabels,
    needsBasedFiltersOptionsMap,
    setNeedsBasedFiltersOptionsMap,
  } = useNeedsBasedFilters({
    filterDataMap,
    filtersToRemoveMap,
    isOverlayFiltersDisplayed,
    needsBasedLabelsFromQueryParams,
    needsBasedOptionsFromQueryParams,
    setFiltersToRemoveMap,
  })

  const mobileFiltersOpened = useRef(false)

  useEffect(() => {
    if (isOverlayFiltersDisplayed && !mobileFiltersOpened.current) {
      setFiltersSnapshotMap({
        basic: filterDataMap.basic,
        needsBased: filterDataMap.needsBased,
        needsBasedQueryValues: structuredClone(filterDataMap.needsBasedQueryValues),
        currentNeedsBasedOptions: structuredClone(needsBasedFiltersOptionsMap.currentOptions),
      })
      mobileFiltersOpened.current = true
    } else if (!isOverlayFiltersDisplayed) {
      mobileFiltersOpened.current = false
      setFiltersToRemoveMap({ basic: {}, needsBased: {}, needsBasedQueryValues: {} })
      setFiltersSnapshotMap({
        basic: filterDataMap.basic,
        needsBased: filterDataMap.needsBased,
        needsBasedQueryValues: structuredClone(filterDataMap.needsBasedQueryValues),
        currentNeedsBasedOptions: structuredClone(needsBasedFiltersOptionsMap.currentOptions),
      })
    }
  }, [
    isOverlayFiltersDisplayed,
    filterDataMap.basic,
    mobileFiltersOpened,
    filterDataMap.needsBased,
    filterDataMap.needsBasedQueryValues,
    needsBasedFiltersOptionsMap,
  ])

  useEffect(() => {
    const clonedData = merge(structuredClone(filterDataMap.basic), filterDataMap.needsBased)
    const updatedNeedsBasedSelectedMap = {}
    const { currentOptions } = needsBasedFiltersOptionsMap

    for (const questionText in currentOptions) {
      let label: string | undefined

      const isSelected =
        !!currentOptions[questionText].length &&
        currentOptions[questionText].every((option) => {
          const { valueBool, valueStr, from, to, bucketLabel, code, externalId, componentType } =
            option
          label = bucketLabel
          const optionValue = getFilterAttributeValue({ valueBool, valueStr, from, to })

          if (code && externalId && componentType === 'dropdown') {
            return checkToFromValues({
              filterData: clonedData,
              externalId,
              to,
              from,
            })
          }

          return code && externalId && clonedData?.[externalId]?.[code]?.includes(optionValue)
        })

      if (label) {
        updatedNeedsBasedSelectedMap[questionText] = {
          [label]: isSelected,
        }
      }
    }

    setNeedsBasedSelectedMap((previousSelectedMap) => {
      const resetPreviousSelectedMap = updateNestedValue(
        previousSelectedMap,
        false
      ) as SelectedNbfFilters
      return merge(structuredClone(resetPreviousSelectedMap), updatedNeedsBasedSelectedMap)
    })
  }, [filterDataMap.basic, filterDataMap.needsBased, needsBasedFiltersOptionsMap])

  return {
    filterDataMap,
    setFilterDataMap,
    filtersSnapshotMap,
    handleNeedsBasedFilters,
    setPreviousNeedsBasedFilterLabels,
    needsBasedFiltersOptionsMap,
    setNeedsBasedFiltersOptionsMap,
    setIsOverlayFiltersDisplayed,
    isOverlayFiltersDisplayed,
    showMoreFilters,
    setShowMoreFilters,
    basicFilterData: filterDataMap.basic,
    needsBasedFilterData: filterDataMap.needsBased,
    selectedNeedsBasedFilters: needsBasedSelectedMap,
    filtersToRemoveMap,
    setFiltersToRemoveMap,
    pageNumberOnFilterInteraction,
    setPageNumberOnFilterInteraction,
  }
}
